import { Chip, Tooltip, Box, Typography, Stack } from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import type { RaRecord } from 'react-admin';
import { InlinePair } from '../../shared/listCells';
import { plural } from '../../shared/plural';
import { contactCodeLabel } from '../../shared/contactCodes';

export type LooseRecord = RaRecord & Record<string, unknown>;

const asText = (v: unknown): string | null =>
  typeof v === 'string' && v.trim() !== '' ? v : null;

/** Nazwa z numerem podatkowym pod spodem — po nich planista rozpoznaje firmę. */
export const NameCell = ({ record }: { record: LooseRecord }) => (
  <Box>
    <Typography variant="body2" sx={{ fontWeight: 600 }}>
      {asText(record.legalName) ?? '—'}
    </Typography>
    {asText(record.taxId) && (
      <Typography variant="caption" color="text.secondary">
        NIP {String(record.taxId)}
      </Typography>
    )}
  </Box>
);

/** Nazwa z NIP-em w jednej linii — wersja dla listy, pełna nazwa w podpowiedzi. */
export const NameInlineCell = ({ record }: { record: LooseRecord }) => (
  <InlinePair
    primary={asText(record.legalName) ?? '—'}
    secondary={asText(record.taxId) ? `NIP ${String(record.taxId)}` : null}
    maxWidth={{ xs: 320, lg: 440, xl: 620 }}
  />
);

/** Miasto i kraj w jednej komórce. */
export const AddressCell = ({ record }: { record: LooseRecord }) => {
  const place = [asText(record.city), asText(record.country)]
    .filter(Boolean)
    .join(', ');
  return place ? (
    <span style={{ whiteSpace: 'nowrap' }}>{place}</span>
  ) : (
    <Typography variant="caption" color="text.secondary">
      —
    </Typography>
  );
};

/**
 * Stan kontrahenta — blokada i flaga z ich kartoteki.
 *
 * Blokada jest NASZA, `active` pochodzi od nich. To dwie różne rzeczy i nie
 * zlewamy ich w jeden znacznik: kontrahent może być u nich nieaktywny, a nie
 * zablokowany przez nas, i odwrotnie.
 */
export const StatusCell = ({ record }: { record: LooseRecord }) => (
  <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
    {record.blacklisted === true && (
      <Tooltip title={asText(record.blacklistReason) ?? 'bez podanego powodu'}>
        <Chip size="small" color="error" icon={<BlockIcon />} label="zablokowany" />
      </Tooltip>
    )}
    {record.active === false && (
      <Chip size="small" variant="outlined" color="warning" label="nieaktywny w TMS" />
    )}
    {/* „Czynny” tylko wtedy, gdy TMS to podał. Pusta flaga to „nie wiemy”,
        a nie „czynny” — znacznik przy braku danych udawał wiedzę (lista
        komunikatów 01.10.2026, „Problemy poza brzmieniem”). */}
    {record.blacklisted !== true && record.active === true && (
      <Chip size="small" variant="outlined" color="success" label="czynny" />
    )}
  </Stack>
);

/**
 * Licznik kontaktów i zleceń — po nim widać, czy kartoteka jest wypełniona.
 * Z odmianą („1 kontakt”, „3 kontakty”) zamiast „kontakt(y)” (07.10.2026).
 */
export const CountsCell = ({ record }: { record: LooseRecord }) => {
  const c = record._count as { contacts?: number; userOffers?: number } | undefined;
  return (
    <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
      {plural(c?.contacts ?? 0, 'kontakt', 'kontakty', 'kontaktów')} ·{' '}
      {plural(c?.userOffers ?? 0, 'zlecenie', 'zlecenia', 'zleceń')}
    </Typography>
  );
};

/**
 * Rodzaj kontaktu — etykieta po polsku z kodu TMS.
 *
 * Kody na `M` to osoby po stronie Marathona, kody na `C` — osoby u klienta;
 * znaczenie skrótów potwierdził Marathon (odpowiedzi 16.10.2026, pyt. 33e;
 * lista: `shared/contactCodes.ts`). Do 16.10 pokazywaliśmy sam kod, bo
 * rozwinięcie było naszym domysłem. Kod z TMS zostaje w dymku.
 */
export const ContactKindChip = ({ kod: code }: { kod: unknown }) => {
  const k = asText(code);
  if (!k) return null;
  return (
    <Tooltip title={`Kod w TMS: ${k}`}>
      <Chip size="small" variant="outlined" label={contactCodeLabel(k) ?? k} />
    </Tooltip>
  );
};

/**
 * Typ klienta z TMS — wartości dosłownie, pod nimi zdanie z rozdziału Trade
 * (`customerTypeHint` z API). Podpowiedź, nie reguła podwykonawcy.
 */
export const CustomerTypes = ({ record }: { record: LooseRecord }) => {
  const types = Array.isArray(record.tmsActivities)
    ? (record.tmsActivities as unknown[]).filter((t): t is string => typeof t === 'string' && t.trim() !== '')
    : [];
  if (types.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" data-testid="contractor-customer-types">
        TMS nie podaje typu klienta
      </Typography>
    );
  }
  const hint = typeof record.customerTypeHint === 'string' ? record.customerTypeHint : null;
  return (
    <Box data-testid="contractor-customer-types">
      <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
        {types.map((t) => (
          <Chip key={t} size="small" variant="outlined" label={t} />
        ))}
      </Stack>
      {hint && (
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5 }}>
          {hint}
        </Typography>
      )}
    </Box>
  );
};
